import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { MarketingScreen } from './screens/MarketingScreen';
import { PrivacyScreen } from './screens/PrivacyScreen';
import { TermsScreen } from './screens/TermsScreen';
import { SupportScreen } from './screens/SupportScreen';
import { AiPolicyScreen } from './screens/AiPolicyScreen';
import { ExamModeInfoScreen } from './screens/ExamModeInfoScreen';
import { SchoolsScreen } from './screens/SchoolsScreen';
import { HomeScreen } from './screens/HomeScreen';
import { CaptureScreen } from './screens/CaptureScreen';
import { ReviewScreen } from './screens/ReviewScreen';
import { AnswerScreen } from './screens/AnswerScreen';
import { ExportScreen } from './screens/ExportScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { ToolsScreen } from './screens/ToolsScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { SignInScreen } from './screens/SignInScreen';
import { AuthCallbackScreen } from './screens/AuthCallbackScreen';
import { PaywallScreen } from './screens/PaywallScreen';
import { AgeCaptureScreen } from './screens/AgeCaptureScreen';
import { ParentalConsentPendingScreen } from './screens/ParentalConsentPendingScreen';
import { TermsAcceptanceScreen } from './screens/TermsAcceptanceScreen';
import { ExamModeIntroScreen } from './screens/ExamModeIntroScreen';
import { ExamModeEndScreen } from './screens/ExamModeEndScreen';
import { SchoolDashboardScreen } from './screens/SchoolDashboardScreen';
import { NotFoundScreen } from './screens/NotFoundScreen';
import { WorksheetProvider } from './context/WorksheetContext';
import { AuthProvider } from './context/AuthContext';
import { ExamModeProvider } from './context/ExamModeContext';
import { BottomNav } from './components/BottomNav';
import { RequireAccess } from './components/RequireAccess';
import { telemetry } from './lib/telemetry';

// Routing:
//   /              marketing landing (parents read this)
//   /privacy       privacy policy
//   /terms         terms of service
//   /support       support and contact page
//   (no public /pricing route - product is marketed as free; paywall only appears
//    in-app after the user has finished FREE_USES worksheets, see src/lib/access.ts)
//   /exam-mode     public explainer of Exam Mode for schools / parents
//   /app           tools dashboard (home of the portfolio)
//   /app/capture   camera and safeguarding gate
//   /app/review    captured photo + cloud opt-in
//   /app/answer    dictation / typing loop, question by question
//   /app/export    final PDF / PNG export and share
//   /app/history   saved worksheets
//   /app/tools     full portfolio with coming-soon tools
//   /app/settings  device settings, privacy, danger zone
//   /app/exam/start  pre-attempt consent + candidate / paper details
//   /app/exam/end    end-of-attempt screen with reason + audit summary
//   /app/school      SENCO dashboard - invite pupils, manage seats, usage
function AppRouteWrapper() {
  return (
    <RequireAccess>
      <WorksheetProvider>
        <Outlet />
        <BottomNav />
      </WorksheetProvider>
    </RequireAccess>
  );
}

function PaywallRouteWrapper() {
  return (
    <WorksheetProvider>
      <Outlet />
      <BottomNav />
    </WorksheetProvider>
  );
}

const router = createBrowserRouter([
  { path: '/', element: <MarketingScreen /> },
  { path: '/privacy', element: <PrivacyScreen /> },
  { path: '/terms', element: <TermsScreen /> },
  { path: '/support', element: <SupportScreen /> },
  { path: '/ai-policy', element: <AiPolicyScreen /> },
  { path: '/exam-mode', element: <ExamModeInfoScreen /> },
  { path: '/schools', element: <SchoolsScreen /> },
  { path: '/signin', element: <SignInScreen /> },
  { path: '/auth/callback', element: <AuthCallbackScreen /> },
  {
    path: '/app/paywall',
    element: <PaywallRouteWrapper />,
    children: [{ index: true, element: <PaywallScreen /> }],
  },
  {
    path: '/app',
    element: <AppRouteWrapper />,
    children: [
      { index: true, element: <HomeScreen /> },
      // Compliance-gate screens - reachable inside RequireAccess so they can
      // read the auth state, but RequireAccess doesn't redirect away from them.
      { path: 'age-capture', element: <AgeCaptureScreen /> },
      { path: 'parental-consent-pending', element: <ParentalConsentPendingScreen /> },
      { path: 'accept-terms', element: <TermsAcceptanceScreen /> },
      { path: 'capture', element: <CaptureScreen /> },
      { path: 'review', element: <ReviewScreen /> },
      { path: 'answer', element: <AnswerScreen /> },
      { path: 'export', element: <ExportScreen /> },
      { path: 'history', element: <HistoryScreen /> },
      { path: 'tools', element: <ToolsScreen /> },
      { path: 'settings', element: <SettingsScreen /> },
      { path: 'exam/start', element: <ExamModeIntroScreen /> },
      { path: 'exam/end', element: <ExamModeEndScreen /> },
      { path: 'school', element: <SchoolDashboardScreen /> },
    ],
  },
  { path: '*', element: <NotFoundScreen /> },
]);

export default function App() {
  useEffect(() => {
    telemetry.track('app_opened', { platform: detectPlatform() });
  }, []);

  // GA4 page_view on client-side route changes.
  // The initial page_view is sent by the gtag config in index.html.
  useEffect(() => {
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== 'function') return;
    const unsubscribe = router.subscribe((state) => {
      gtag('event', 'page_view', {
        page_path: state.location.pathname + state.location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    });
    return () => unsubscribe();
  }, []);

  return (
    <AuthProvider>
      <ExamModeProvider>
        <RouterProvider router={router} />
      </ExamModeProvider>
    </AuthProvider>
  );
}

function detectPlatform(): 'ios' | 'android' | 'web' {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'web';
}
